import { useCallback, useEffect, useState } from "react";
import { Loader2, Scale } from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { SettleItemDialog } from "./SettleItemDialog";
import { FailureActions } from "./FailureActions";
import { GroupSettlementTable, SettlementTimeline } from "./GroupAndTimeline";
import { GroupSettleDialog } from "./GroupSettleDialog";
import { SettlementEmailButton } from "./SettlementEmailButton";
import { CustomerFinancialSummary } from "./CustomerFinancialSummary";
import { STATUS_STYLE, money, settle, statusLabel } from "./settlementApi";

const FIGURES = [
  ["original_rent", "Original rent"], ["final_rent", "Final rent"], ["rent_paid_original", "Rent paid"],
  ["extra_charges_total", "Extra charges"], ["extra_charges_paid", "Charges paid"], ["original_bond", "Bond"],
  ["bond_deducted", "Bond used"], ["bond_refunded", "Bond refunded"], ["bond_remaining", "Bond left"],
  ["rental_credit_issued", "Credit issued"], ["rent_credit_redeemed", "Credit used"], ["outstanding", "Outstanding"],
];

export const SettlementBadge = ({ status }) =>
  status ? <Badge className={`border text-[9px] uppercase ${STATUS_STYLE[status] || ""}`} data-testid="settlement-status-badge">{statusLabel(status)}</Badge> : null;

function StuckBanner({ booking, summary, onDone }) {
  const [doc, setDoc] = useState(null);
  useEffect(() => { if (summary.settlement_key) settle.commitDoc(summary.settlement_key).then(setDoc).catch(() => setDoc({})); }, [summary.settlement_key]);
  if (!doc) return null;
  const detail = { message: doc.last_error ? `Last attempt failed at ${doc.failed_step}: ${typeof doc.last_error === "string" ? doc.last_error : JSON.stringify(doc.last_error)}` : "A settlement is still marked as in progress.", payments_kept: doc.kept_charge_ids };
  return <FailureActions bookingId={booking.id} groupId={doc.group_id} settlementKey={summary.settlement_key} body={doc.body} detail={detail} onDone={onDone} />;
}

export function SettlementPanel({ booking, onChanged }) {
  const [summary, setSummary] = useState(null);
  const [group, setGroup] = useState(null);
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const gid = booking.admin_booking_group_id || booking.cart_group_id;

  const load = useCallback(async () => {
    if (gid) setGroup(null);
    const [s, t] = await Promise.all([settle.summary(booking.id), settle.timeline(booking.id)]);
    setSummary(s); setEvents(t);
    if (gid) settle.group(gid).then(setGroup).catch(() => setGroup({ error: true }));
  }, [booking.id, gid]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged?.(); };

  if (!summary) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F]" /></div>;
  const stuck = ["settlement_failed", "settlement_pending"].includes(summary.settlement_status);
  return (
    <div className="mt-5 space-y-5 pr-2 min-w-0 max-w-full overflow-x-hidden" data-testid="settlement-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Scale className="w-4 h-4 text-[#9A7B4F]" /><SettlementBadge status={summary.settlement_status} /></div>
        <Button onClick={() => setOpen(true)} disabled={stuck} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] tracking-wider uppercase" data-testid="amend-settle-btn">Amend / Settle item</Button>
      </div>
      {stuck && <StuckBanner booking={booking} summary={summary} onDone={refresh} />}
      <div className="grid grid-cols-3 gap-px bg-[#282828]/8 border border-[#282828]/8" data-testid="settlement-figures">
        {FIGURES.map(([k, l]) => (
          <div key={k} className="bg-white px-3 py-2">
            <div className="text-[9px] uppercase tracking-wider text-[#676664]/55">{l}</div>
            <div className={`text-[13px] tabular-nums ${k === "outstanding" && summary[k] > 0 ? "text-amber-700 font-semibold" : "text-[#282828]"}`} data-testid={`settlement-fig-${k}`}>{money(summary[k])}</div>
          </div>
        ))}
      </div>
      <GroupSettlementTable group={group} />
      {group?.items?.length > 1 && (
        <div className="flex flex-wrap gap-2 justify-end">
          <Button onClick={() => setGroupOpen(true)} disabled={group.items.some((i) => ["settlement_failed", "settlement_pending"].includes(i.settlement_status))} className="bg-[#282828] hover:bg-black text-white text-[10px] tracking-wider uppercase" data-testid="settle-all-items-btn">Settle all items</Button>
          <SettlementEmailButton groupId={gid} label="Email group summary" testid="email-group-summary-btn" />
        </div>
      )}
      <div className="flex justify-end"><SettlementEmailButton bookingId={booking.id} label="Email this item's summary" testid="email-item-summary-btn" /></div>
      <CustomerFinancialSummary email={booking.customer_email} refreshKey={events} />
      {group?.error && <div className="text-[11px] text-red-600" data-testid="group-settlement-error">Couldn't load the group totals — reopen the booking to try again.</div>}
      {gid && !group && <div className="text-[11px] text-[#676664]/50 flex items-center gap-2" data-testid="group-settlement-loading"><Loader2 className="w-3 h-3 animate-spin" /> Loading group totals…</div>}
      <div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-[#9A7B4F] mb-3">Financial timeline</div>
        <SettlementTimeline events={events} />
      </div>
      {open && <SettleItemDialog open={open} onClose={() => setOpen(false)} booking={booking} summary={summary} onSaved={refresh} />}
      {groupOpen && group?.items && <GroupSettleDialog open={groupOpen} onClose={() => setGroupOpen(false)} group={group} onSaved={refresh} />}
    </div>
  );
}
